// Общий источник публичных auth-параметров с бэка (GET /api/v1/auth/config) —
// один запрос на все формы входа/регистрации: приветственный бейдж
// (useWelcomeCredits) И конфиг Turnstile (useTurnstile) читают ЭТОТ хук,
// а не дёргают ручку каждый по отдельности.
import { useEffect, useState } from 'react'
import api from '../api/client'

export interface TurnstileConfig {
	enabled: boolean
	sitekey: string | null
	alwaysOn: string[]
	loginMode: 'adaptive' | 'always' | 'off'
}

export interface AuthConfig {
	welcomeCredits: number
	turnstile: TurnstileConfig
}

interface AuthConfigResponse {
	welcome_credits: number
	turnstile?: {
		enabled: boolean
		sitekey?: string
		always_on?: string[]
		login_mode?: string
	}
}

const FALLBACK: AuthConfig = {
	welcomeCredits: 40,
	turnstile: { enabled: false, sitekey: null, alwaysOn: [], loginMode: 'off' },
}

let cache: AuthConfig | null = null
let inflight: Promise<AuthConfig> | null = null

function normalize(res: AuthConfigResponse): AuthConfig {
	const t = res.turnstile
	return {
		welcomeCredits: typeof res.welcome_credits === 'number' && res.welcome_credits >= 0 ? res.welcome_credits : 40,
		turnstile: t?.enabled && t.sitekey
			? { enabled: true, sitekey: t.sitekey, alwaysOn: t.always_on || [], loginMode: (t.login_mode as TurnstileConfig['loginMode']) || 'adaptive' }
			: { enabled: false, sitekey: null, alwaysOn: [], loginMode: 'off' },
	}
}

function fetchAuthConfig(): Promise<AuthConfig> {
	if (cache) return Promise.resolve(cache)
	if (!inflight) {
		inflight = api
			.request<AuthConfigResponse>('/api/v1/auth/config', { skipAuth: true })
			.then((res) => {
				cache = normalize(res)
				return cache
			})
			.catch(() => FALLBACK)
			.finally(() => { inflight = null })
	}
	return inflight
}

/** null — ответ ещё не пришёл (ни один потребитель не должен рендерить
 *  зависящий от него UI до этого момента, чтобы не мигать фолбэком). */
export function useAuthConfig(): AuthConfig | null {
	const [config, setConfig] = useState<AuthConfig | null>(cache)
	useEffect(() => {
		if (cache) return
		let cancelled = false
		fetchAuthConfig().then((res) => { if (!cancelled) setConfig(res) })
		return () => { cancelled = true }
	}, [])
	return config
}
